import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { 
  UserIcon, 
  ClockIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PlayIcon,
  StopIcon,
  ChartBarIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeSlashIcon,
  UserGroupIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import Pagination from '../common/Pagination';

const Employees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // User management state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'EMPLOYEE',
    jobRole: '',
    location: '',
    hourlyWage: '',
    accessLevel: 'EMPLOYEE',
    startDate: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('metrics'); // 'metrics' or 'management'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [jobRoles, setJobRoles] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [roleError, setRoleError] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [locationError, setLocationError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the unsaved changes hook - only active when modal is open
  useUnsavedChanges(hasUnsavedChanges, showModal);

  // Apply pagination for employees (card view)
  const totalEmployees = employees.length;
  const employeeStartIndex = (currentPage - 1) * itemsPerPage;
  const employeeEndIndex = employeeStartIndex + itemsPerPage;
  const paginatedEmployees = employees.slice(employeeStartIndex, employeeEndIndex);

  // Apply pagination for users (table view)
  const totalUsers = users.length;
  const userStartIndex = (currentPage - 1) * itemsPerPage;
  const userEndIndex = userStartIndex + itemsPerPage;
  const paginatedUsers = users.slice(userStartIndex, userEndIndex);

  // Reset to page 1 when switching tabs
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    fetchEmployees();
    fetchUsers();
    fetchJobRoles();
    fetchLocations();
  }, []);

  // Handle edit user from navigation state
  useEffect(() => {
    if (location.state?.editUserId && users.length > 0) {
      const userToEdit = users.find(user => user.id.toString() === location.state.editUserId);
      if (userToEdit) {
        setActiveTab('management'); // Switch to management tab
        handleEdit(userToEdit);
        // Clear the state to prevent re-opening on refresh
        navigate('/employees', { replace: true });
      }
    }
  }, [location.state, users, navigate]);

  // Track changes to form data
  useEffect(() => {
    if (showModal) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalFormData, showModal]);

  // Update current time every minute for active sessions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/timesheets/admin/employees');
      setEmployees(response.data.employees);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load user data');
    }
  };

  const fetchJobRoles = async () => {
    try {
      const response = await axios.get('/api/job-roles');
      setJobRoles(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching job roles:', err);
      // If 403, user doesn't have permission - silently set empty array
      if (err.response && err.response.status === 403) {
        console.log('User does not have permission to view job roles');
      }
      setJobRoles([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      // If 403, user doesn't have permission - silently set empty array
      if (err.response && err.response.status === 403) {
        console.log('User does not have permission to view locations');
      }
      setLocations([]);
    }
  };

  const createNewRole = async () => {
    if (!newRoleName.trim()) {
      setRoleError(true);
      return;
    }

    try {
      await axios.post('/api/job-roles', {
        name: newRoleName.trim(),
        description: `Custom job role: ${newRoleName.trim()}`
      });
      await fetchJobRoles(); // Refresh the job roles list
      setFormData({ ...formData, jobRole: newRoleName.trim() });
      setShowRoleModal(false);
      setNewRoleName('');
      setRoleError(false);
    } catch (roleError) {
      console.error('Error creating job role:', roleError);
      alert('Failed to create job role. Please try again.');
    }
  };

  const createNewLocation = async () => {
    if (!newLocationName.trim()) {
      setLocationError(true);
      return;
    }

    try {
      await axios.post('/api/locations', {
        name: newLocationName.trim(),
        description: `Custom location: ${newLocationName.trim()}`
      });
      await fetchLocations(); // Refresh the locations list
      setFormData({ ...formData, location: newLocationName.trim() });
      setShowLocationModal(false);
      setNewLocationName('');
      setLocationError(false);
    } catch (locationError) {
      console.error('Error creating location:', locationError);
      alert('Failed to create location. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (editingUser) {
        // For editing, use regular endpoint (no username/password changes in this form)
        await axios.put(`/api/users/${editingUser.id}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          jobRole: formData.jobRole,
          location: formData.location || '',
          hourlyWage: formData.hourlyWage,
          accessLevel: formData.accessLevel,
          startDate: formData.startDate
        });
      } else {
        // For new employees, email is required and username/password will be set via signup flow
        const userData = {
          ...formData,
          location: formData.location || '',
          hourlyWage: formData.hourlyWage ? parseFloat(formData.hourlyWage) : undefined
        };
        await axios.post('/api/users/invite', userData);
      }
      setShowModal(false);
      setEditingUser(null);
      const resetFormData = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'EMPLOYEE',
        jobRole: '',
        location: '',
        hourlyWage: '',
        accessLevel: 'EMPLOYEE',
        startDate: ''
      };
      setFormData(resetFormData);
      setOriginalFormData(resetFormData);
      setHasUnsavedChanges(false);
      setSuccessMessage(editingUser ? 'Employee updated successfully!' : 'Employee created and invitation email sent!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchUsers();
      fetchEmployees(); // Refresh employee metrics too
    } catch (err) {
      console.error('Error saving user:', err);
      console.error('Error response:', err.response?.data);

      // Show detailed error information
      let errorMessage = 'Failed to save employee';
      if (err.response?.data?.errors) {
        // Validation errors array
        const validationErrors = err.response.data.errors.map(e => e.msg).join(', ');
        errorMessage = `Validation errors: ${validationErrors}`;
      } else if (err.response?.data?.message) {
        // Single error message
        errorMessage = err.response.data.message;
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const editFormData = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      jobRole: user.jobRole || '',
      location: user.location || '',
      hourlyWage: user.hourlyWage?.toString() || '',
      accessLevel: user.accessLevel || 'EMPLOYEE',
      startDate: user.startDate ? new Date(user.startDate).toISOString().split('T')[0] : ''
    };
    setFormData(editFormData);
    setOriginalFormData({ ...editFormData });
    setHasUnsavedChanges(false);
    setShowModal(true);
    setSuccessMessage(''); // Clear any previous success message
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`/api/users/${userId}`);
      fetchUsers();
      fetchEmployees(); // Refresh employee metrics too
    } catch (err) {
      console.error('Error deleting user:', err);
      console.error('Delete error response:', err.response?.data);
      console.error('Delete error status:', err.response?.status);
      
      let errorMessage = 'Failed to delete employee';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status) {
        errorMessage = `Server error (${err.response.status}): ${err.response?.statusText || 'Unknown error'}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this employee? They will not be able to log in.')) {
      return;
    }
    try {
      await axios.patch(`/api/users/${userId}/deactivate`);
      fetchUsers();
      fetchEmployees(); // Refresh employee metrics too
    } catch (err) {
      console.error('Error deactivating user:', err);
      const errorMessage = err.response?.data?.message || 'Failed to deactivate employee';
      alert(errorMessage);
    }
  };


  const handleResendInvite = async (userId) => {
    if (!window.confirm('Are you sure you want to resend the invitation email?')) {
      return;
    }
    
    try {
      await axios.post(`/api/users/${userId}/resend-invite`);
      setSuccessMessage('Invitation email resent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error resending invite:', err);
      const errorMessage = err.response?.data?.message || 'Failed to resend invitation';
      alert(errorMessage);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCurrentSessionDuration = (clockIn) => {
    if (!clockIn) return 0;
    const hours = (currentTime - new Date(clockIn)) / (1000 * 60 * 60);
    return hours.toFixed(2);
  };

  const getStatusColor = (isClockedIn) => {
    return isClockedIn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (isClockedIn) => {
    return isClockedIn ? (
      <PlayIcon className="h-4 w-4" />
    ) : (
      <StopIcon className="h-4 w-4" />
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={UserIcon}
        title="Error Loading Employees"
        description={error}
        action={{
          label: 'Retry',
          onClick: fetchEmployees
        }}
      />
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Employee Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage employees, monitor timesheets and booking metrics</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => {
                const newFormData = {
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  role: 'EMPLOYEE',
                  jobRole: '',
                  hourlyWage: '',
                  accessLevel: 'EMPLOYEE',
                  startDate: ''
                };
                setFormData(newFormData);
                setOriginalFormData({ ...newFormData });
                setEditingUser(null);
                setHasUnsavedChanges(false);
                setShowModal(true);
                setSuccessMessage(''); // Clear any previous success message
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center text-sm sm:text-base"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Employee</span>
              <span className="sm:hidden">Add</span>
            </button>
            <button
              onClick={() => {
                fetchEmployees();
                fetchUsers();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center text-sm sm:text-base"
            >
              <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-3 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'metrics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="h-5 w-5 mr-2 inline" />
              Metrics & Performance
            </button>
            <button
              onClick={() => setActiveTab('management')}
              className={`py-3 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'management'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserGroupIcon className="h-5 w-5 mr-2 inline" />
              User Management ({users.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Metrics Tab Content */}
      {activeTab === 'metrics' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <UserIcon className="h-8 w-8 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Total Employees</p>
                    <p className="text-2xl font-bold text-blue-900">{summary.totalEmployees}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <ClockIcon className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Currently Clocked In</p>
                    <p className="text-2xl font-bold text-green-900">{summary.currentlyClockedIn}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <CalendarIcon className="h-8 w-8 text-purple-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">Active Bookings</p>
                    <p className="text-2xl font-bold text-purple-900">{summary.totalActiveBookings}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-8 w-8 text-yellow-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Total Revenue</p>
                    <p className="text-2xl font-bold text-yellow-900">${summary.totalRevenue.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employees Metrics List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Employee Performance Details ({employees.length})
              </h2>
            </div>
        
        {employees.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={UserIcon}
              title="No Employees Found"
              description="No active employees found in the system."
            />
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
            {paginatedEmployees.map((employee) => (
              <div key={employee.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-medium text-blue-700">
                          {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-lg font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(employee.metrics.timesheets.isCurrentlyClockedIn)}`}>
                          {getStatusIcon(employee.metrics.timesheets.isCurrentlyClockedIn)}
                          <span className="ml-1">
                            {employee.metrics.timesheets.isCurrentlyClockedIn ? 'Clocked In' : 'Clocked Out'}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div>
                          <span className="font-medium">Username:</span> {employee.username}
                        </div>
                        <div>
                          <span className="font-medium">Email:</span> {employee.email}
                        </div>
                        <div>
                          <span className="font-medium">Member Since:</span> {formatDate(employee.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Session Info */}
                {employee.metrics.timesheets.isCurrentlyClockedIn && employee.metrics.timesheets.currentSession && (
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">Current Session</p>
                        <p className="text-sm text-blue-600">
                          Started: {formatDateTime(employee.metrics.timesheets.currentSession.clockIn)}
                        </p>
                        <p className="text-sm text-blue-600">
                          Duration: {getCurrentSessionDuration(employee.metrics.timesheets.currentSession.clockIn)} hours
                        </p>
                      </div>
                      <ClockIcon className="h-6 w-6 text-blue-500" />
                    </div>
                  </div>
                )}

                {/* Metrics Grid */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Timesheet Metrics */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      Timesheet Metrics
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Total Sessions:</span>
                        <span className="ml-2 font-medium">{employee.metrics.timesheets.totalSessions}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Completed:</span>
                        <span className="ml-2 font-medium">{employee.metrics.timesheets.completedSessions}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Hours:</span>
                        <span className="ml-2 font-medium">{employee.metrics.timesheets.totalHours} hrs</span>
                      </div>
                      <div>
                        <span className="text-gray-600">This Week:</span>
                        <span className="ml-2 font-medium">{employee.metrics.timesheets.weeklyHours} hrs</span>
                      </div>
                      <div>
                        <span className="text-gray-600">With Location:</span>
                        <span className="ml-2 font-medium">{employee.metrics.timesheets.sessionsWithLocation}</span>
                      </div>
                    </div>
                  </div>

                  {/* Booking Metrics */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Booking Metrics
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Total Bookings:</span>
                        <span className="ml-2 font-medium">{employee.metrics.bookings.total}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Active:</span>
                        <span className="ml-2 font-medium">{employee.metrics.bookings.active}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Completed:</span>
                        <span className="ml-2 font-medium">{employee.metrics.bookings.completed}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Cancelled:</span>
                        <span className="ml-2 font-medium">{employee.metrics.bookings.cancelled}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Total Revenue:</span>
                        <span className="ml-2 font-medium text-green-600">${employee.metrics.bookings.totalRevenue}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination for Employee Cards */}
          <Pagination
            currentPage={currentPage}
            totalItems={totalEmployees}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
          </>
        )}
        </div>
        </>
      )}

      {/* Management Tab Content */}
      {activeTab === 'management' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Team Members ({users.length})
            </h2>
          </div>
          
          {users.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={UserGroupIcon}
                title="No Employees Found"
                description="No employees have been added yet."
              />
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-auto max-h-[600px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Employee
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Contact
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Access
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Location
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Role
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Wage
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      {/* Employee */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-700">
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-2 min-w-0 flex-1">
                            <button
                              onClick={() => navigate(`/employees/${user.id}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block"
                              title={`${user.firstName} ${user.lastName}`}
                            >
                              {user.firstName} {user.lastName}
                            </button>
                            <div className="text-xs text-gray-500 truncate" title={user.username}>
                              {(!user.isActive || user.username.includes('_')) ? 
                                <span className="italic">@pending-setup</span> : 
                                <span>@{user.username}</span>
                              }
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Contact */}
                      <td className="px-3 py-3">
                        <div className="text-xs text-gray-900">
                          <div className="truncate" title={user.email}>{user.email}</div>
                          {user.phone && <div className="text-gray-500 truncate" title={user.phone}>{user.phone}</div>}
                        </div>
                      </td>
                      
                      {/* Access */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.accessLevel === 'MANAGER' ? 'bg-purple-100 text-purple-800' : 
                          user.role === 'BUSINESS_ADMIN' ? 'bg-red-100 text-red-800' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'BUSINESS_ADMIN' ? 'Admin' : 
                           user.accessLevel === 'MANAGER' ? 'Mgr' : 
                           'Emp'}
                        </span>
                      </td>
                      
                      {/* Location */}
                      <td className="px-3 py-3 text-xs text-gray-900 truncate" title={user.location || 'Not specified'}>
                        {user.location || 'None'}
                      </td>
                      
                      {/* Role */}
                      <td className="px-3 py-3 text-xs text-gray-900 truncate" title={user.jobRole || 'Not assigned'}>
                        {user.jobRole || 'None'}
                      </td>
                      
                      {/* Wage */}
                      <td className="px-3 py-3 text-xs text-gray-900">
                        {user.hourlyWage ? (
                          <span className="font-medium">${parseFloat(user.hourlyWage).toFixed(0)}</span>
                        ) : (
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            title="Add Wage"
                          >
                            Add
                          </button>
                        )}
                      </td>
                      
                      {/* Status */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          (user.isActive && !user.username.includes('_')) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {(user.isActive && !user.username.includes('_')) ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      
                      {/* Actions */}
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit employee"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          
                          {/* Show resend invite button for pending users */}
                          {(!user.isActive || user.username.includes('_')) && (
                            <button
                              onClick={() => handleResendInvite(user.id)}
                              className="text-orange-600 hover:text-orange-800"
                              title="Resend invitation email"
                            >
                              <EnvelopeIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {(user.isActive && !user.username.includes('_')) && (
                            <button
                              onClick={() => handleDeactivate(user.id)}
                              className="text-yellow-600 hover:text-yellow-800"
                              title="Deactivate employee"
                            >
                              <EyeSlashIcon className="h-4 w-4" />
                            </button>
                          )}
                          {user.role !== 'BUSINESS_ADMIN' && user.role !== 'SUPER_ADMIN' && (
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete employee"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4 p-4">
              {paginatedUsers.map((user) => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="space-y-3">
                    {/* Employee Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-indigo-700">
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="text-base font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              user.role === 'BUSINESS_ADMIN'
                                ? 'bg-purple-100 text-purple-800'
                                : user.accessLevel === 'MANAGER'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.accessLevel || user.role}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact & Details */}
                    <div className="border-t pt-3 space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500 w-20">Email:</span>
                        <span className="text-gray-900 truncate">{user.email || '—'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 w-20">Phone:</span>
                        <span className="text-gray-900">{user.phone || '—'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 w-20">Location:</span>
                        <span className="text-gray-900">{user.location || '—'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 w-20">Role:</span>
                        <span className="text-gray-900">{user.jobRole || '—'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 w-20">Wage:</span>
                        <span className="text-gray-900">
                          {user.hourlyWage ? `$${parseFloat(user.hourlyWage).toFixed(2)}/hr` : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t flex space-x-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium py-2 px-3 rounded-lg text-sm transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => navigate(`/employees/${user.id}`)}
                        className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium py-2 px-3 rounded-lg text-sm transition-colors"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination for Users Table */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalUsers}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
            </>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-2 sm:px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            {/* Modal panel - Mobile Optimized */}
            <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full border border-gray-200">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
                  {/* Header - Mobile Optimized */}
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                      <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                      <span className="truncate">
                        {editingUser ? 'Edit Employee' : 'Add New Employee'}
                      </span>
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 sm:p-1 -m-2 sm:m-0 touch-manipulation"
                      aria-label="Close form"
                    >
                      <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    {/* Information Notice - Mobile Optimized */}
                    {!editingUser && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-blue-800">
                              <strong>New signup process:</strong> After adding an employee, they will receive an email invitation to set up their own username and password.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Basic Information - Mobile Optimized */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            First Name *
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                            placeholder="Enter first name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                            placeholder="Enter last name"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                          placeholder="Enter email address"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Job Details Section - Mobile Optimized */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Job Details</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Role Dropdown */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Role
                            </label>
                            <select
                              value={formData.jobRole}
                              onChange={(e) => {
                                if (e.target.value === 'add_new') {
                                  setShowRoleModal(true);
                                  setNewRoleName('');
                                  setRoleError(false);
                                } else {
                                  setFormData({ ...formData, jobRole: e.target.value });
                                }
                              }}
                              className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                            >
                              {jobRoles.length === 0 ? (
                                <>
                                  <option value="">Select or Add Role</option>
                                  <option value="add_new">Add New Role</option>
                                </>
                              ) : (
                                <>
                                  <option value="">Select Role</option>
                                  {jobRoles.map(role => (
                                    <option key={role.id} value={role.name}>{role.name}</option>
                                  ))}
                                  <option value="add_new">Add New Role</option>
                                </>
                              )}
                            </select>
                          </div>

                          {/* Location Dropdown */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Location
                            </label>
                            <select
                              value={formData.location}
                              onChange={(e) => {
                                if (e.target.value === 'add_new') {
                                  setShowLocationModal(true);
                                  setNewLocationName('');
                                  setLocationError(false);
                                } else {
                                  setFormData({ ...formData, location: e.target.value });
                                }
                              }}
                              className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                            >
                              {locations.length === 0 ? (
                                <>
                                  <option value="">Select or Add Location</option>
                                  <option value="add_new">Add New Location</option>
                                </>
                              ) : (
                                <>
                                  <option value="">Select Location</option>
                                  {locations.map(location => (
                                    <option key={location.id} value={location.name}>{location.name}</option>
                                  ))}
                                  <option value="add_new">Add New Location</option>
                                </>
                              )}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Wage */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Hourly Wage
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.hourlyWage}
                                onChange={(e) => setFormData({ ...formData, hourlyWage: e.target.value })}
                                className="w-full pl-8 pr-4 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                                placeholder="15.00"
                              />
                            </div>
                          </div>

                          {/* Rate Type (Display Only) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Rate Type
                            </label>
                            <div className="w-full px-4 py-3 sm:py-2 text-base sm:text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-600">
                              Per Hour
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Access Level Section - Mobile Optimized */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Access Level</h3>
                      <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-3 sm:space-y-0">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="accessLevel"
                            value="EMPLOYEE"
                            checked={formData.accessLevel === 'EMPLOYEE'}
                            onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
                            className="mr-3 sm:mr-2 text-blue-600 focus:ring-blue-500 touch-manipulation"
                          />
                          <span className="text-base sm:text-sm">Employee</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="accessLevel"
                            value="MANAGER"
                            checked={formData.accessLevel === 'MANAGER'}
                            onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
                            className="mr-3 sm:mr-2 text-blue-600 focus:ring-blue-500 touch-manipulation"
                          />
                          <span className="text-base sm:text-sm">Manager</span>
                        </label>
                      </div>
                    </div>

                    {/* Start Date Section - Mobile Optimized */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Start Date</h3>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                      />
                    </div>
                  </div>

                  {/* Actions - Mobile Optimized */}
                  <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:py-3 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-3 sm:py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                    >
                      {isSubmitting ? 'Saving...' : (editingUser ? 'Update Employee' : 'Add Employee')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (hasUnsavedChanges) {
                          if (window.confirm('Your changes have not been saved - are you sure you want to leave this page?')) {
                            setShowModal(false);
                            setEditingUser(null);
                            const resetFormData = {
                              firstName: '',
                              lastName: '',
                              email: '',
                              phone: '',
                              role: 'EMPLOYEE',
                              jobRole: '',
                              hourlyWage: '',
                              accessLevel: 'EMPLOYEE',
                              startDate: ''
                            };
                            setFormData(resetFormData);
                            setOriginalFormData(resetFormData);
                            setHasUnsavedChanges(false);
                          }
                        } else {
                          setShowModal(false);
                          setEditingUser(null);
                          const resetFormData = {
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            role: 'EMPLOYEE',
                            jobRole: '',
                            hourlyWage: '',
                            accessLevel: 'EMPLOYEE',
                            startDate: ''
                          };
                          setFormData(resetFormData);
                          setOriginalFormData(resetFormData);
                          setHasUnsavedChanges(false);
                        }
                      }}
                      disabled={isSubmitting}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-3 sm:py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add New Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-2">Add new role</h2>
            <p className="text-sm text-gray-600 mb-4">New role name</p>
            
            <div className="mb-4">
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => {
                  setNewRoleName(e.target.value);
                  if (e.target.value.trim()) {
                    setRoleError(false);
                  }
                }}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
                  roleError || (!newRoleName.trim() && newRoleName !== '')
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter role name"
              />
              
              {(roleError || (!newRoleName.trim() && newRoleName !== '')) && (
                <div className="flex items-center mt-2 text-red-600">
                  <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">This field is required</span>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowRoleModal(false);
                  setNewRoleName('');
                  setRoleError(false);
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createNewRole}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
              >
                Add new role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-2">Add new location</h2>
            <p className="text-sm text-gray-600 mb-4">New location name</p>
            
            <div className="mb-4">
              <input
                type="text"
                value={newLocationName}
                onChange={(e) => {
                  setNewLocationName(e.target.value);
                  if (e.target.value.trim()) {
                    setLocationError(false);
                  }
                }}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
                  locationError || (!newLocationName.trim() && newLocationName !== '')
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter location name"
              />
              
              {(locationError || (!newLocationName.trim() && newLocationName !== '')) && (
                <div className="flex items-center mt-2 text-red-600">
                  <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">This field is required</span>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowLocationModal(false);
                  setNewLocationName('');
                  setLocationError(false);
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createNewLocation}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
              >
                Add new location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees; 